import z from 'zod'
import {
  nullSafeBoolean,
  nullSafeNumber,
  nullSafeOptionalStringArray,
  nullSafeStringArray,
} from '../shared/schema'
import { SessionMuting, ChatbotProvider } from '../../types'

const sessionMutingSchema = z.nativeEnum(SessionMuting)
const chatbotProviderSchema = z.nativeEnum(ChatbotProvider)

const automaticRoomNameValuesSchema = z
  .object({
    adjectives: nullSafeStringArray([]),
    nouns: z.record(z.string(), z.string()),
  })
  .optional()

export const schema = z.object({
  adminRoleManagementEnabled: nullSafeBoolean(false),
  automaticRoomNameValues: z.preprocess((val) => {
    if (val) {
      const parsed = automaticRoomNameValuesSchema.safeParse(val)
      // if it's correctly formatted, use it
      // in any other case, undefined
      if (parsed.success) return parsed.data
    }

    return undefined
  }, automaticRoomNameValuesSchema),
  animationMethod: nullSafeBoolean(true),
  generateGoogleSlides: nullSafeBoolean(false),
  generateSessionSummary: nullSafeBoolean(true),
  rubricTestDesiredStdDeviation: nullSafeNumber(1.5),
  rubricTestMeanEpsilon: nullSafeNumber(0.9),
  sendInstructorEmails: nullSafeBoolean(false),
  sessionMuting: z.preprocess(
    (val) => val ?? SessionMuting.none,
    sessionMutingSchema
  ),
  sessionResultsGroupBalanceCoefficient: nullSafeNumber(0.5),
  sessionResultsShowAllConversationTooltips: nullSafeBoolean(true),
  showRubricArgumentEventMatchMinimum: nullSafeNumber(1), // float value
  useStrictPublishRequirements: nullSafeBoolean(false),
  // BL-4956: Learning Objective rubrics (type 7) are internal-testing only.
  // While this is false, a deck containing one cannot be published; flip it to
  // true to graduate the type to production.
  enableLearningObjectivesRubrics: nullSafeBoolean(false),
  showOrganizationsUI: nullSafeBoolean(false),
  audioSilenceDetectionEnabled: nullSafeBoolean(false),
  transcriptionLanguages: nullSafeOptionalStringArray(),
  // How long a "we can't hear you" problem must persist, with no success in
  // between, before the dialog opens. 0 disables the dialog. Shared by both
  // watchers that raise one - the transcription watcher and speech
  // reachability - because it is one dialog, with one repair, and an operator
  // who wants it wants it for either fault.
  transcriptProblemWaitDurationMS: nullSafeNumber(0), // in ms
  // When true, verifies that locally-detected speech (meeting_user_spoke_locally)
  // is confirmed by the server's active-speaker detection (meeting_user_spoke).
  // The master switch for that check - detection, analytics and its half of the
  // dialog above - and so the way to turn off the reachability trigger alone.
  spokeLocallyVerificationEnabled: nullSafeBoolean(true),
  // How long to wait after an un-muted local utterance before checking whether
  // the server confirmed it, absorbing delivery jitter between the local mic
  // event and the server active-speaker signal. In ms; negatives clamp to 0.
  // Deliberately generous: a delay that is too short reports healthy audio as
  // unreachable, writing a false meeting_user_spoke_not_confirmed row - and now
  // that a run of those can open a dialog, telling a user nobody can hear them
  // while the room hears them fine. Lower it once the confirmed/unconfirmed
  // split has proven stable.
  spokeLocallyVerificationDelayMS: nullSafeNumber(10000).transform((val) =>
    val < 0 ? 0 : val
  ),
  verifyInvitation: nullSafeBoolean(false),
  showAssignmentCSVResultsInMinutesAndSortingAsPercent: nullSafeBoolean(false),
  // timeout in seconds, cannot be negative
  soloBotNudgeButtonTimeout: nullSafeNumber(0).transform((val) =>
    val < 0 ? 0 : val
  ),
  // Background-blur intensity passed to LiveKit's BackgroundProcessor.
  // 0 disables the feature entirely (no UI, no keyboard shortcut).
  backgroundBlurRadius: nullSafeNumber(0).transform((val) =>
    val < 0 ? 0 : val
  ),
  // Kill-switch for Safari only. When true, blur is force-disabled and the
  // UI is hidden for Safari users, regardless of `backgroundBlurRadius`.
  disableBackgroundBlurInSafari: nullSafeBoolean(false),
  hideSlideDeckPrice: nullSafeBoolean(false),
  useNextbookImportForm: nullSafeBoolean(false),
  enhancedSupportLinkParams: nullSafeBoolean(true),
  useSharedGoogleDocSlide: nullSafeBoolean(false),
  serviceWorkerKillSwitch: nullSafeBoolean(false),
  useNewGradingApi: nullSafeBoolean(false),
  useApiOnProfilePage: nullSafeBoolean(false),
  instructorsWhoCanViewTranscripts: nullSafeOptionalStringArray(),
  useV2SlideDeckCatalog: nullSafeBoolean(false),
  // ENG-277: the admin JSON import screens send the JSON to the Adonis server,
  // which writes Postgres and Firestore. When false they run the original
  // browser importer (importSlideDeckFromJson.ts).
  useServerSlideDeckImport: nullSafeBoolean(false),
  // Gates the customizable grading controls (per-rubric weights + score
  // mapping). When false, the grading dialog hides the scoring section and the
  // "customize weights" link and reverts to the legacy (narrower) width.
  customRubricsEnabled: nullSafeBoolean(false),
  // Pre-session AV check: when true the microphone check mark requires the
  // speech detector (VolumeAnalyzerWorklet) to fire on the preview track
  // instead of a single instant of raw analyser energy. A dead mic that
  // produces crackle has energy but no speech, so the raw check passes it
  // (see docs/incidents/support/2026-07-05-solo-room-no-transcription.md).
  avCheckSpeechDetectionEnabled: nullSafeBoolean(false),
  // In-meeting garbage-mic detection for solo rooms: if the user stays
  // unmuted for this long without a single transcript coming back, emit the
  // transcriptProblem flow ("we can't hear you"). 0 disables the check.
  unmutedNoTranscriptProblemDurationMS: nullSafeNumber(0), // in ms
  // When true, the A/V-room caption overlay consumes the WebVTT file for each
  // slide video instead of the legacy slide_caption Firestore subcollection.
  consumeVttCaptions: nullSafeBoolean(false),
  showExternalDemoToggle: nullSafeBoolean(false),
  // Gates the student's "Appeal final score" action on Session Results. When
  // false the score-breakdown dialog's footer renders Close only, as it does
  // today.
  studentGradeAppealsEnabled: nullSafeBoolean(false),
  enableAppealOverrideCSVColumn: nullSafeBoolean(false),
  aiGradeExplanationsEnabled: nullSafeBoolean(false),
  aiGradeExplanationRefreshEnabled: nullSafeBoolean(false),
  gradeExplanationAccessEnabled: nullSafeBoolean(false),

  // Which support chatbot the web app mounts: 'hubspot', 'fin', or nothing.
  //
  // Fails closed on purpose. A typo, an empty string, a stale value, or an
  // absent flag all resolve to `none` and mount no chatbot at all - a bot only
  // ever appears because someone chose it explicitly. Note this means the flag
  // must be set in Firestore before any chatbot shows up.
  chatbotProvider: z.preprocess((val) => {
    if (val === ChatbotProvider.hubspot || val === ChatbotProvider.fin)
      return val
    return ChatbotProvider.none
  }, chatbotProviderSchema),
  // Gates the admin "Content Library" sidebar item and its pages (Slide Decks,
  // Global Authors, Global Assets). Admin-only; super admins see it even when
  // this is off. See apps/web/src/hooks/contentLibraryAccess.ts.
  contentLibraryEnabled: nullSafeBoolean(false),
  // BL-4870: when true, a scheduled session opens at its scheduled time
  // instead of ROOM_CAN_START_MINUTES_BEFORE_SCHEDULED_AT minutes early, and
  // the A/V room check holds students back until then. When false every room
  // keeps the early-access buffer, which is the behaviour that shipped before
  // this flag existed. Demo rooms always keep the buffer either way.
  enforceScheduledSessionStart: nullSafeBoolean(false),
  // BL-5103: when true, students redeem access codes (section, organization
  // and Breakout-level) through the Adonis endpoint instead of the
  // redeemSectionPassCoupon cloud function. Breakout-level codes only redeem
  // on the Adonis path. Flip back to false as a kill switch.
  redeemAccessCodesOnServer: nullSafeBoolean(false),
  // ENG-414: when true, the admin slide-deck editor saves decks, slides,
  // slide order, questions, rubrics and rubric tests only through the Adonis
  // server (ENG-424), and the server copies each save to Firestore inside its
  // Postgres transaction. The server and the admin app read this one flag, so
  // they switch together and Firestore is never written twice. Flip back to
  // false to roll back (ENG-426).
  adminSlideDeckSavesThroughServer: nullSafeBoolean(false),
  // ENG-479: when true, the class page's Participants tab and the assignment
  // page's Gradebook tab read from the Adonis API (Postgres) instead of the
  // Firestore cubits. A CX user can override it for one browser tab; see
  // cubits/resultsReadSource.ts.
  readResultsFromApi: nullSafeBoolean(false),
})

export const empty = Object.freeze(schema.parse({}))

export type FirestoreSettingsFeatureFlags = z.infer<typeof schema>
